// prune-dead-avatar-mode-env-placeholder.ts
//
// Ticket #139 — vì sao cần module này:
// `avatar init` sinh `.claude/settings.json` từ template từng khai
// `"AVATAR_MODE": "{{mode}}"`, trong khi biến `mode` đã bị gỡ khỏi variable builder
// từ v3 (init-scaffold-variable-builders.ts:50). Engine template CỐ Ý giữ nguyên
// placeholder không khớp ⇒ workspace nhận env var mang chuỗi literal `{{mode}}`,
// không code path nào đọc. Gỡ key khỏi template chỉ chữa được workspace MỚI: merge
// env lúc sync chỉ THÊM var mà pack có còn user chưa có (settings-field-mergers.ts:60),
// không bao giờ bỏ var đã nằm sẵn trong file ⇒ máy member vẫn giữ rác. Module này
// self-heal file đã tồn tại; caller là `avatar sync`.
//
// BẤT BIẾN AN TOÀN — xoá theo GIÁ TRỊ, KHÔNG theo tên key:
// điều kiện xoá là giá trị phải là chuỗi và BẰNG nguyên văn placeholder. Không so
// khớp bộ phận, không chuẩn hoá khoảng trắng, không biểu thức chính quy. Lý do:
// settings.json chứa API key của member — sai phạm vi là xoá nhầm cấu hình người ta
// tự đặt. Giá trị khác placeholder ⇒ GIỮ NGUYÊN, không ghi file.
//
// Fail-soft: không throw ở bất kỳ nhánh nào. Settings vắng hoặc hỏng JSON đều trả
// status cho caller log. Đường ghi (backup → atomic write) CHỈ chạy khi khớp chính xác.
//
// Purity: module CHỈ prune. Log/report là việc của caller — cùng kiểu phân vai với
// pruneOrphanToolHooks (prune) vs sync tail (state flip).

import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";
import type { PackSettingsTemplate } from "./merge-settings-types.js";
import { backupFilename } from "./settings-merge-primitives.js";
import { withSettingsFileLock } from "./with-settings-file-lock.js";
import { SECRET_FILE_MODE } from "./write-claude-settings-json-per-project.js";

export const DEAD_MODE_ENV_KEY = "AVATAR_MODE";
export const DEAD_MODE_PLACEHOLDER_VALUE = "{{mode}}";

export type PruneDeadModeStatus = "pruned" | "no-op" | "settings-unreadable";

export interface PruneDeadModeResult {
  status: PruneDeadModeStatus;
  removed: boolean;
  // BOOLEAN, không bao giờ mang giá trị lạ ra ngoài: user có thể đã nhét secret
  // vào key này, mà result thì đi thẳng vào log của sync.
  keptForeignValue?: boolean;
  changes: string[];
  backupPath?: string;
  settingsMissing?: boolean;
  warning?: string;
}

function projectSettingsPath(workspacePath: string): string {
  return join(workspacePath, ".claude", "settings.json");
}

function noOp(): PruneDeadModeResult {
  return { status: "no-op", removed: false, changes: [] };
}

export async function pruneDeadAvatarModeEnvPlaceholder(
  workspacePath: string,
): Promise<PruneDeadModeResult> {
  const settingsPath = projectSettingsPath(workspacePath);
  // Không có settings.json → không có gì để dọn (workspace chưa init xong).
  if (!(await pathExists(settingsPath))) {
    return { ...noOp(), settingsMissing: true };
  }

  // #164 writer #5: read (:67) + write (:104) trong CÙNG bracket lock. Module này
  // chạy CUỐI đường `avatar sync`, ngay sau prune-orphan-hooks — không lock thì
  // snapshot của nó ghi đè đúng hook mà reapply vừa đặt vào.
  return await withSettingsFileLock(workspacePath, () => pruneUnderLock(settingsPath));
}

// Thân cũ — dịch nguyên văn, KHÔNG đổi logic (kể cả mọi nhánh fail-soft).
async function pruneUnderLock(settingsPath: string): Promise<PruneDeadModeResult> {
  let settings: PackSettingsTemplate;
  try {
    settings = await readJson<PackSettingsTemplate>(settingsPath);
  } catch (err) {
    // File hỏng JSON → KHÔNG throw, KHÔNG ghi, KHÔNG tạo backup. Sync sau thử lại
    // khi file parse được (cùng cách xử lý với prune-orphan-tool-hooks).
    return {
      status: "settings-unreadable",
      removed: false,
      changes: [],
      warning: `settings.json không parse được: ${err instanceof Error ? err.message : String(err)} — sync sau sẽ thử lại`,
    };
  }

  const env = settings.env as Record<string, unknown> | undefined;
  if (!env || typeof env !== "object" || Array.isArray(env)) return noOp();

  const value = env[DEAD_MODE_ENV_KEY];
  if (value === undefined) return noOp();

  // ⚠ GIỮ guard kiểu chuỗi — đừng tin khai báo type. `PackSettingsTemplate.env`
  // khai `Record<string, string>` nhưng đó là view LỎNG, không phải sự thật runtime:
  // settings.json ngoài đời có value KIỂU SỐ trong khối env. readJson trả thẳng dữ
  // liệu JSON nên `value` có thể là number/boolean/object. Bỏ guard này ⇒ module vỡ
  // giả định ngay trên dữ liệu thật. Case C12 trong test canh đúng dòng dưới đây.
  if (typeof value !== "string" || value !== DEAD_MODE_PLACEHOLDER_VALUE) {
    return { ...noOp(), keptForeignValue: true };
  }

  // Bỏ key bằng rest-destructuring — convention codebase, không dùng toán tử xoá
  // thuộc tính. `env` rỗng sau khi bỏ thì VẪN giữ `env: {}` (không omit key):
  // applyScalarFieldMerges xử lý object rỗng bình thường, và giữ key thì ít bất ngờ
  // hơn cho người đọc file. Spread bảo toàn vị trí + thứ tự các key còn lại.
  const { [DEAD_MODE_ENV_KEY]: _dead, ...restEnv } = env;
  const merged: Record<string, unknown> = { ...settings, env: restEnv };

  // Backup trước, rồi atomic write — đường ghi DUY NHẤT của module.
  const backupPath = backupFilename(settingsPath);
  await fs.copyFile(settingsPath, backupPath);
  // #164 chốt (f): giữ mode 0600 — file này chứa API key của member.
  await writeJsonAtomic(settingsPath, merged, SECRET_FILE_MODE);

  return {
    status: "pruned",
    removed: true,
    changes: [
      `removed dead env var ${DEAD_MODE_ENV_KEY}="${DEAD_MODE_PLACEHOLDER_VALUE}" (placeholder v3, không code path nào đọc)`,
    ],
    backupPath,
  };
}
